// 세리사무소 포털 AI 대화 — 게이트웨이 RAG 채팅(SSE)을 실제로 호출하는 화면.
//
// 기본 홈(Home.startview)의 AI 는 실서비스 호출과 데모 연출(ProActive 카드·MOU 패널·전표 슬롯·
// SLOT_RESPONSE_DATA)이 한 몸이라, 그 화면을 베끼면 연출까지 따라온다. 그래서 복사하지 않고
// 실제로 동작하는 알맹이만 같은 규칙으로 다시 조립했다:
//   · 대화 ID(newConversationId)를 유지하는 동안 서버가 멀티턴 맥락을 기억한다
//   · 문서는 RAG 색인(ingestAttachments), 이미지는 축소본을 채팅 본문에 직접 싣는다
//   · 이미지는 IMAGE_TURN_LIMIT 턴까지만 재전송하고, 이후는 색인된 전사 텍스트가 검색으로 답한다
//   · 첨부를 × 로 빼면 색인도 함께 내려간다(deleteDoc)
//
// 호출·첨부·이미지 유틸은 기본 홈과 '같은 모듈'을 그대로 가져다 쓴다. 이미지 장수 한도나
// 색인 단계 문구가 바뀌어도 두 포털이 조용히 갈라지지 않도록, 공유되는 코드는 한 벌만 둔다.
// 갈라도 되는 것은 화면 연출뿐이라 그것만 이 파일과 Home.zeirishi.module.css 에 있다.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { deleteDoc, newConversationId, streamChat } from '../../api/aiChat';
import { isImageFile, isSupportedImage, prepareChatImage } from '../../utils/image';
import LLMSelector from '../../components/Home/LLMSelector';
import RichText from '../../components/AI/RichText';
import { AttachmentStrip, ingestAttachments, toAttachments } from './Home.attachments';
import { IMAGE_TURN_LIMIT, MAX_CHAT_IMAGES } from './Home.data';
import type { ConversationAttachment } from './Home.types';
import styles from './Home.zeirishi.module.css';

/** 한 턴의 메시지 — 사용자 질문 또는 AI 응답 */
interface ChatMsg {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** assistant — 서버가 SSE(event:step)로 내리는 진행 단계 문구 */
  step?: string;
  /** assistant — stopped 는 사용자가 멈춘 것이라 그때까지 받은 텍스트를 남긴다 */
  status?: 'streaming' | 'done' | 'stopped' | 'error';
  /** assistant — status==='error' 일 때 사유 */
  error?: string;
  /** user — 이 턴에 함께 실어 보낸 이미지의 미리보기 URL */
  images?: string[];
}

export interface ZeirishiAiChatProps {
  /** 대화가 비었을 때 띄우는 인사말 */
  greeting?: string;
  /** 인사말 아래 한 줄 설명 */
  subGreeting?: string;
  /** 예시 질문 칩 — 누르면 그대로 전송된다 */
  suggestions?: string[];
  /** 입력창 자리표시 문구 */
  placeholder?: string;
  /**
   * 바깥(포털 상단 검색바)에서 넣어 주는 질문.
   * seq 가 바뀔 때만 전송한다 — 같은 문장을 다시 물어도 보내야 하므로 텍스트가 아니라 번호를 본다.
   */
  outbound?: { text: string; seq: number };
}

let msgSeq = 0;
const newMsgId = () => `zr-m${++msgSeq}`;

export default function ZeirishiAiChat({
  greeting = '세리사무소 업무를 도와드릴게요',
  subGreeting = '자료를 첨부하면 내용을 색인해 근거와 함께 답합니다. 문서는 검색 대상이 되고, 이미지는 모델이 직접 봅니다.',
  suggestions = [],
  placeholder = '질문하거나 업무를 요청해보세요...',
  outbound,
}: ZeirishiAiChatProps) {
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);

  /* 대화 식별자 — 같은 값을 유지하는 동안 서버가 멀티턴 맥락을 기억한다.
     서버가 로그인 사용자 UUID 와 합성해 저장하므로 계정이 다르면 같은 ID 여도 격리된다. */
  const conversationIdRef = useRef(newConversationId());

  /* 진행 중인 턴의 일련번호 — "정지"와 "새 대화"가 이 값을 올려 이전 턴의 콜백을 무효로 만든다.
     공용 streamChat 에 중단 신호를 넘기는 경로가 없어(그 파일은 이 화면 밖의 코드다)
     소켓 자체를 끊지는 못한다. 남은 토큰은 서버에서 계속 오지만 화면에 반영하지 않는다. */
  const turnRef = useRef(0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /* 한글·일본어 IME 조합 중 Enter 가 전송으로 새지 않도록 */
  const composingRef = useRef(false);
  const streamingRef = useRef(false);
  streamingRef.current = streaming;

  /* ── 대화 첨부 ──
     문서와 이미지를 한 목록으로 다룬다. 둘 다 대화에 붙어 색인되므로 전송해도 사라지지 않고,
     사용자가 × 로 뺄 때 화면과 색인에서 함께 내려간다. */
  const [attachments, setAttachments] = useState<ConversationAttachment[]>([]);
  const [attachNotice, setAttachNotice] = useState<string | null>(null);
  const attachmentsRef = useRef<ConversationAttachment[]>([]);
  attachmentsRef.current = attachments;

  /* 색인 진행 반영 — 완료 시 받은 docId 를 보관한다(취소할 때 이 값으로 색인을 내린다) */
  const applyIngest = useCallback(
    (uid: string, ingest: ConversationAttachment['ingest'], docId?: string) => {
      setAttachments(prev => prev.map(a => (a.uid === uid ? { ...a, ingest, ...(docId ? { docId } : {}) } : a)));
    },
    [],
  );

  /**
   * 첨부 처리 — 문서·이미지 모두 '원본'을 색인하고, 이미지는 채팅에 실을 축소본을 따로 만든다.
   * 색인에 축소본을 넘기지 않는 이유: 전사 정확도가 원본에서 낫고,
   * 색인 title 이 원본 파일명이어야 답변의 [출처: …] 칩이 제대로 붙는다.
   */
  const attachFiles = useCallback((rawFiles: File[]) => {
    const images = rawFiles.filter(isImageFile);
    const docs = rawFiles.filter(f => !isImageFile(f));
    const usable = images.filter(isSupportedImage);

    const notices: string[] = [];
    if (images.length > usable.length) {
      notices.push('이미지는 PNG·JPEG·WebP·GIF만 첨부할 수 있습니다');
    }
    const room = MAX_CHAT_IMAGES - attachmentsRef.current.filter(a => a.kind === 'image').length;
    const acceptedImages = usable.slice(0, Math.max(0, room));
    if (usable.length > room) {
      notices.push(`이미지는 ${MAX_CHAT_IMAGES}장까지 첨부할 수 있습니다`);
    }
    setAttachNotice(notices.length > 0 ? notices.join(' · ') : null);

    const toIngest = [...docs, ...acceptedImages];
    if (toIngest.length === 0) {
      return;
    }
    const entries = toAttachments(toIngest);
    setAttachments(prev => [...prev, ...entries]);
    ingestAttachments(toIngest, entries, conversationIdRef.current, applyIngest);

    // 이미지는 채팅 본문에도 실어야 하므로 축소본을 별도 경로로 만든다(색인과 무관)
    acceptedImages.forEach(file => {
      const entry = entries.find(e => e.name === file.name && e.kind === 'image');
      prepareChatImage(file)
        .then(img =>
          setAttachments(prev =>
            prev.map(a =>
              a.uid === entry?.uid
                ? { ...a, previewUrl: img.previewUrl, image: { mimeType: img.mimeType, data: img.data, turns: 0 } }
                : a,
            ),
          ),
        )
        .catch(() => setAttachNotice(`'${file.name}' 이미지를 불러오지 못했습니다`));
    });
  }, [applyIngest]);

  /**
   * 첨부 취소 — 목록에서 빼고 색인도 내린다.
   * 색인 삭제가 실패하면 목록에 되돌린다: 지운 줄 알았는데 계속 답변 근거로 잡히는 것보다
   * 아직 남아 있다고 정직하게 보여주는 편이 낫다.
   */
  const removeAttachment = useCallback(async (uid: string) => {
    setAttachNotice(null);
    const target = attachmentsRef.current.find(a => a.uid === uid);
    if (!target) {
      return;
    }
    setAttachments(prev => prev.filter(a => a.uid !== uid));
    if (!target.docId) {
      return;   // 아직 색인 전이거나 색인에 실패한 첨부 — 내릴 것이 없다
    }
    try {
      await deleteDoc(target.docId, conversationIdRef.current);
    } catch (e) {
      setAttachments(prev => (prev.some(a => a.uid === uid) ? prev : [...prev, target]));
      setAttachNotice(e instanceof Error ? e.message : '첨부를 취소하지 못했습니다');
    }
  }, []);

  /* ── 전송 ── */
  const send = useCallback((raw: string) => {
    const q = raw.trim();
    if (!q || streamingRef.current) {
      return;
    }

    /* 서버 대화 메모리에는 텍스트만 남으므로 활성 이미지는 매 턴 다시 싣는다.
       다만 원본은 몇 턴까지만 — 이후에는 색인된 전사 텍스트가 검색으로 답한다.
       목록에서 빼지는 않는다: 색인은 남아 있고, 사용자가 직접 뺄 때까지
       "이 대화에 붙어 있다"는 표시는 유지해야 한다. */
    const live = attachmentsRef.current.filter(a => a.image && a.image.turns < IMAGE_TURN_LIMIT);
    const turnImages = live.map(a => ({ mimeType: a.image!.mimeType, data: a.image!.data }));
    if (turnImages.length > 0) {
      setAttachments(prev =>
        prev.map(a =>
          a.kind === 'image' && a.image && a.image.turns < IMAGE_TURN_LIMIT
            ? { ...a, image: { ...a.image, turns: a.image.turns + 1 } }
            : a,
        ),
      );
    }

    const answerId = newMsgId();
    setMessages(prev => [
      ...prev,
      {
        id: newMsgId(),
        role: 'user',
        text: q,
        images: live.map(a => a.previewUrl).filter((u): u is string => !!u),
      },
      { id: answerId, role: 'assistant', text: '', status: 'streaming' },
    ]);
    setDraft('');
    setStreaming(true);

    const turn = ++turnRef.current;
    /* 이 턴이 아직 화면의 주인인가 — 정지·새 대화 이후 도착한 토큰을 버리는 기준 */
    const owns = () => turnRef.current === turn;

    const patch = (fn: (m: ChatMsg) => Partial<ChatMsg>) => {
      if (!owns()) return;
      setMessages(prev => prev.map(m => (m.id === answerId ? { ...m, ...fn(m) } : m)));
    };

    /* 응답 하나는 한 번만 마무리한다 — onDone 과 then 이 겹쳐 들어올 수 있다 */
    let settled = false;
    const finish = (status: ChatMsg['status'], error?: string) => {
      if (settled || !owns()) return;
      settled = true;
      patch(() => ({ status, step: undefined, ...(error ? { error } : {}) }));
      setStreaming(false);
    };

    streamChat({
      conversationId: conversationIdRef.current,
      message: q,
      images: turnImages,
      onStep: step => patch(() => ({ step })),
      onChunk: chunk => patch(m => ({ text: m.text + chunk })),
      onDone: () => finish('done'),
    })
      .then(() => finish('done'))   // 종료 이벤트 없이 스트림이 닫힌 경우까지 마무리
      .catch(err => finish('error', err instanceof Error ? err.message : String(err)));
  }, []);

  /* 바깥에서 넣어 준 질문 전송 — 상단 검색바에서 물으면 이 대화로 들어온다.
     이미 처리한 seq 는 흘려보낸다: 부모가 리렌더될 때마다 같은 질문이 다시 나가면 안 된다. */
  const outboundSeqRef = useRef(0);
  useEffect(() => {
    if (!outbound || outbound.seq === outboundSeqRef.current) {
      return;
    }
    outboundSeqRef.current = outbound.seq;
    send(outbound.text);
  }, [outbound, send]);

  /* 정지 — 이 턴을 화면에서 놓아 준다(남은 토큰은 무시). 받은 답변은 그대로 남긴다 */
  const stop = useCallback(() => {
    turnRef.current++;
    setMessages(prev => prev.map(m => (m.status === 'streaming' ? { ...m, status: 'stopped', step: undefined } : m)));
    setStreaming(false);
  }, []);

  /* 새 대화 — 대화 ID 를 새로 딴다.
     첨부는 이전 대화 ID 에 묶여 있어 새 대화에서는 검색되지 않으므로 목록도 함께 비운다.
     색인을 지우지는 않는다 — 서버가 대화 범위로 보관하고, 이 화면에서 다시 볼 일이 없다. */
  const newChat = useCallback(() => {
    turnRef.current++;
    conversationIdRef.current = newConversationId();
    setMessages([]);
    setAttachments([]);
    setAttachNotice(null);
    setDraft('');
    setStreaming(false);
    setTimeout(() => textareaRef.current?.focus(), 30);
  }, []);

  /* 자동 스크롤 — 사용자가 위를 읽는 중이면 끌어내리지 않는다 */
  const stickRef = useRef(true);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !stickRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  /* 입력창 높이 자동 조절 — 여러 줄을 쓰면 늘고, 비우면 한 줄로 돌아온다 */
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [draft]);

  const empty = messages.length === 0;

  return (
    <div className={styles.panel}>
      <div
        className={styles.scroll}
        ref={scrollRef}
        onScroll={e => {
          const el = e.currentTarget;
          stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
      >
        {empty ? (
          <div className={styles.empty}>
            <div className={styles.emptyMark}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="#3B82F6">
                <path d="M12 2l1.5 8.5L22 12l-8.5 1.5L12 22l-1.5-8.5L2 12l8.5-1.5z" />
              </svg>
            </div>
            <h1 className={styles.emptyTitle}>{greeting}</h1>
            {subGreeting && <p className={styles.emptySub}>{subGreeting}</p>}
            {suggestions.length > 0 && (
              <div className={styles.suggestions}>
                {suggestions.map(s => (
                  <button key={s} type="button" className={styles.suggestion} onClick={() => send(s)}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.thread}>
            {messages.map(m =>
              m.role === 'user' ? (
                <div key={m.id} className={styles.userRow}>
                  <div className={styles.userWrap}>
                    {m.images && m.images.length > 0 && (
                      <div className={styles.userImages}>
                        {m.images.map(url => (
                          <img key={url} src={url} alt="" className={styles.userImage} />
                        ))}
                      </div>
                    )}
                    <div className={styles.userBubble}>{m.text}</div>
                  </div>
                </div>
              ) : (
                <div key={m.id} className={styles.answer}>
                  <div className={styles.answerHead}>
                    <span className={styles.answerIcon}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="#3B82F6">
                        <path d="M12 2l1.5 8.5L22 12l-8.5 1.5L12 22l-1.5-8.5L2 12l8.5-1.5z" />
                      </svg>
                    </span>
                    {m.status === 'streaming' ? (
                      <>
                        {/* key 를 단계 문구로 둬, 문구가 바뀔 때마다 등장 애니메이션이 다시 돈다 */}
                        <span key={m.step} className={styles.answerStep}>{m.step || '응답 생성 중...'}</span>
                        <span className={styles.dots}><i /><i /><i /></span>
                      </>
                    ) : m.status === 'error' ? (
                      <span className={`${styles.answerTitle} ${styles.err}`}>응답을 받지 못했습니다</span>
                    ) : m.status === 'stopped' ? (
                      <span className={`${styles.answerTitle} ${styles.muted}`}>응답 중단됨</span>
                    ) : (
                      <span className={`${styles.answerTitle} ${styles.done}`}>응답 완료</span>
                    )}
                  </div>
                  <div className={styles.answerBody}>
                    {m.text ? (
                      <RichText text={m.text} />
                    ) : m.status === 'streaming' ? (
                      <div className={styles.skeletons}>
                        <span className={styles.skel} style={{ width: '72%' }} />
                        <span className={styles.skel} style={{ width: '58%' }} />
                        <span className={styles.skel} style={{ width: '85%' }} />
                      </div>
                    ) : null}
                    {m.status === 'error' && <div className={styles.errBox}>{m.error}</div>}
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>

      {/* ── 입력 바 ── */}
      <div className={styles.composerBar}>
        <div className={styles.composer}>
          <AttachmentStrip attachments={attachments} notice={attachNotice} onRemove={removeAttachment} />
          <textarea
            ref={textareaRef}
            className={styles.input}
            rows={1}
            placeholder={placeholder}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onCompositionStart={() => { composingRef.current = true; }}
            onCompositionEnd={() => { composingRef.current = false; }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                if (composingRef.current || (e.nativeEvent as KeyboardEvent).isComposing) return;
                e.preventDefault();
                send(draft);
              }
            }}
          />
          <div className={styles.composerBottom}>
            <div className={styles.composerSide}>
              <button
                type="button"
                className={styles.iconBtn}
                title="자료 첨부 — 문서는 색인 후 검색, 이미지는 모델에 직접 전달"
                onClick={() => fileInputRef.current?.click()}
              >
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                onChange={e => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = '';   // 같은 파일을 다시 골라도 change 가 오도록 비운다
                  textareaRef.current?.focus();
                  attachFiles(files);
                }}
              />
              {!empty && (
                <button type="button" className={styles.textBtn} onClick={newChat} title="대화를 비우고 새 대화를 시작합니다">
                  새 대화
                </button>
              )}
            </div>
            <div className={styles.composerSide}>
              <LLMSelector dropUp={true} />
              {streaming ? (
                <button type="button" className={styles.stopBtn} onClick={stop} title="응답 생성 중단">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.sendBtn}
                  onClick={() => send(draft)}
                  disabled={!draft.trim()}
                  title="전송 (Enter)"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
